import {
  ComponentProps,
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import cn from 'clsx';
import { useTranslation } from 'next-i18next';

import styles from './tabs.module.scss';

type Props = {
  theme:
    | 'light'
    | 'dark'
    | 'top-border'
    | 'bottom-border'
    | 'bottom-border-light';
};
export function Tabs({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn(styles.tabsRoot, className)}
      {...props}
    />
  );
}

export const TabsList = forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & Props
>(({ className, theme = 'light', ...props }, ref) => {
  const {
    i18n: { language },
  } = useTranslation();
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 5,
    top: 4,
    width: 0,
    height: 0,
    opacity: 0,
  });
  const tabsListRef = useRef<HTMLDivElement | null>(null);

  const updateIndicator = useCallback(() => {
    if (!tabsListRef.current) return;

    const activeTab = tabsListRef.current.querySelector<HTMLElement>(
      '[data-state="active"]'
    );
    if (!activeTab) return;

    const activeRect = activeTab.getBoundingClientRect();
    const tabsRect = tabsListRef.current.getBoundingClientRect();

    requestAnimationFrame(() => {
      setIndicatorStyle({
        left: activeRect.left - tabsRect.left,
        top: activeRect.top - tabsRect.top,
        width: activeRect.width,
        height: activeRect.height,
        opacity: 1,
      });
    });
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(updateIndicator, 0);
    window.addEventListener('resize', updateIndicator);

    const observer = new MutationObserver(updateIndicator);
    if (tabsListRef.current) {
      observer.observe(tabsListRef.current, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateIndicator);
      observer.disconnect();
    };
  }, [updateIndicator, language]);

  return (
    <div className={styles.tabsListWrapper} ref={tabsListRef}>
      <TabsPrimitive.List
        ref={ref}
        data-slot="tabs-list"
        className={cn(
          styles.tabsList,
          theme === 'dark' && styles['tabsList--dark'],
          theme === 'top-border' && styles['tabsList--top-border'],
          theme === 'bottom-border' && styles['tabsList--bottom-border'],
          theme === 'bottom-border-light' &&
            styles['tabsList--bottom-border-light'],
          className
        )}
        {...props}
      />
      <div className={styles.tabsIndicator} style={indicatorStyle} />
    </div>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    data-slot="tabs-trigger"
    className={cn(styles.tabsTrigger, className)}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    data-slot="tabs-content"
    className={cn(styles.tabsContent, className)}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;
